import * as Popover from "@radix-ui/react-popover";
import { cn } from "@/utils/cn";
import {
  useState,
  useRef,
  useEffect,
  cloneElement,
  isValidElement,
} from "react";

export interface TooltipProps {
  trigger: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
  delay?: number;
  offset?: number;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  mode?: "hover" | "click" | "hybrid";
  wrapperClassName?: string;
}

export default function Tooltip({
  trigger,
  content,
  disabled = false,
  delay = 200,
  side = "top",
  offset = 10,
  align = "center",
  mode = "hover",
  wrapperClassName = "",
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [isClickLocked, setIsClickLocked] = useState(false); // 追踪是否被点击锁定
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const scheduleHide = () => {
    if ((mode === "hover" || mode === "hybrid") && !isClickLocked) {
      clearHideTimeout();
      timeoutRef.current = setTimeout(() => {
        setOpen(false);
      }, delay);
    }
  };

  // Hover 模式的事件处理
  const handleTriggerMouseEnter = () => {
    if (!disabled && (mode === "hover" || mode === "hybrid")) {
      clearHideTimeout();
      setOpen(true);
    }
  };

  const handleTriggerMouseLeave = () => {
    if (!disabled && (mode === "hover" || mode === "hybrid")) {
      scheduleHide();
    }
  };

  const handleContentMouseEnter = () => {
    if (!disabled && (mode === "hover" || mode === "hybrid")) {
      clearHideTimeout();
    }
  };

  const handleContentMouseLeave = () => {
    if (!disabled && (mode === "hover" || mode === "hybrid")) {
      scheduleHide();
    }
  };

  // Click 模式的事件处理
  const handleTriggerClick = (e: React.MouseEvent) => {
    console.log("Tooltip click triggered", mode);
    if (!disabled && (mode === "click" || mode === "hybrid")) {
      e.preventDefault();
      e.stopPropagation();

      if (mode === "hybrid") {
        // 点击后锁定显示，不管当前状态如何
        clearHideTimeout();
        setIsClickLocked(true);
        setOpen(true);
      } else {
        // 原有的 click 模式逻辑
        setOpen(!open);
      }
    }
  };

  // Click 模式下点击外部关闭
  useEffect(() => {
    if (mode === "click" || mode === "hybrid") {
      const handleClickOutside = () => {
        setOpen(false);
        if (mode === "hybrid") {
          setIsClickLocked(false); // 重置点击锁定状态，恢复到初始 hover 行为
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [mode, open]);

  if (disabled) {
    return <>{trigger}</>;
  }

  // 创建增强的 trigger 元素
  const enhancedTrigger = (() => {
    if (mode === "hover") {
      // Hover 模式：在包装器上处理事件
      return trigger;
    } else {
      // Click 模式或 hybrid 模式：直接在 trigger 元素上添加点击事件
      if (isValidElement(trigger)) {
        return cloneElement(trigger as React.ReactElement, {
          onClick: (e: React.MouseEvent) => {
            // 保留原有的 onClick 事件
            if (trigger.props.onClick) {
              trigger.props.onClick(e);
            }
            handleTriggerClick(e);
          },
        });
      } else {
        // 如果不是有效的 React 元素，用 span 包装
        return (
          <span onClick={handleTriggerClick} style={{ cursor: "pointer" }}>
            {trigger}
          </span>
        );
      }
    }
  })();

  const wrapperProps =
    mode === "hover" || mode === "hybrid"
      ? {
          onMouseEnter: handleTriggerMouseEnter,
          onMouseLeave: handleTriggerMouseLeave,
        }
      : {};

  const contentProps =
    mode === "hover" || mode === "hybrid"
      ? {
          onMouseEnter: handleContentMouseEnter,
          onMouseLeave: handleContentMouseLeave,
        }
      : {};

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div
        {...wrapperProps}
        data-tooltip-trigger
        className={wrapperClassName}
      >
        <Popover.Trigger asChild>{enhancedTrigger}</Popover.Trigger>
      </div>
      <Popover.Portal>
        <Popover.Content
          side={side}
          align={align}
          sideOffset={offset || 12}
          {...contentProps}
          className={cn(
            "z-[9999] max-w-xs text-sm outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2",
            "data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2",
            "data-[side=top]:slide-in-from-bottom-2"
          )}
          collisionPadding={10}
        >
          {content}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
